import type { Mesh } from 'three'
import type { World } from './world'
import { rStats } from '~/lib/three-d-viewer/rStats'
declare global {
  interface Window {
    world: World
  }
}

interface RStatsReturnType {
  start: () => void
  end: () => void
  update: () => void
  tick: () => void
  frame: () => void
  set: (v: any) => void
  value: () => number
}

interface RStatsType {
  (id?: string): RStatsReturnType
}

export class DEBUG {
  private static instance: DEBUG
  private rStats: RStatsType | null = null
  drawCalls: number = 0
  triangles: number = 0
  totalObjects: { [id: string]: Mesh } = {}

  /**
   * The Singleton's constructor should always be private to prevent direct
   * construction calls with the `new` operator.
   */
  private constructor(world: World) {
    const queryString = window.location.search
    const urlParams = new URLSearchParams(queryString)
    const isDebugMode = urlParams.get('debug3d') === 'true'
    if (!isDebugMode) return

    this.rStats = rStats({
      values: {
        r: { caption: 'total render time (ms)', over: 16, max: 20 },
        fps: { caption: 'frames per second (s)', under: 50, max: 60 },
        transparency_time: { caption: 'transition render time (ms)', over: 3, max: 4 },
        billboard_time: { caption: 'billboard render time (ms)', over: 2, max: 2 },
        anim_time: { caption: 'animation render time (ms)', over: 2, max: 2 },
        label_time: { caption: '3D label render time (ms)', over: 2, max: 2 },
        drill_label_time: { caption: 'drill label render time (ms)', over: 2, max: 2 },
        render_time: { caption: 'rendering to canvas time (ms)', over: 10, max: 10 },
        ttl_obj: { caption: 'Total visible objects' },
        geo: { caption: 'geometries' },
        tex: { caption: 'textures' },
        mats: { caption: 'materials' },
        tris: { caption: 'triangles' },
        calls: { caption: 'draw calls' },
      },
      groups: [
        { caption: 'Frame', values: ['r', 'fps'] },
        {
          caption: 'Render Modules',
          values: ['transparency_time', 'billboard_time', 'anim_time', 'label_time', 'drill_label_time', 'render_time'],
        },
        { caption: 'Memory', values: ['ttl_obj', 'geo', 'tex', 'mats'] },
        // { caption: 'Scene Objects', values: ['r'] },
        { caption: 'Low Level', values: ['tris', 'calls'], collapsed: true },
      ],
      fractions: [
        { base: 'r', steps: ['transparency_time', 'billboard_time', 'anim_time', 'label_time', 'drill_label_time', 'render_time'] },
      ],
    })
    window.world = world
  }

  start(id: string) {
    if (!this.rStats) return
    this.rStats(id).start()
  }

  end(id: string) {
    if (!this.rStats) return
    this.rStats(id).end()
  }

  update() {
    if (!this.rStats) return
    this.rStats('ttl_obj').set(Object.keys(this.totalObjects).length + 1)
    this.rStats().update()
    this.totalObjects = {}
  }

  tick(id: string) {
    if (!this.rStats) return
    this.rStats(id).tick()
  }

  frame(id: string) {
    if (!this.rStats) return
    this.rStats(id).frame()
  }

  set(id: string, v: any) {
    if (!this.rStats) return
    this.rStats(id).set(v)
  }

  value(id: string): any {
    if (!this.rStats) return
    return this.rStats(id).value()
  }

  isDebug(): boolean {
    return !!this.rStats || false
  }

  /**
   * The static method that controls the access to the singleton instance.
   *
   * This implementation let you subclass the Singleton class while keeping
   * just one instance of each subclass around.
   */
  public static init(world: World) {
    if (!DEBUG.instance && world) {
      DEBUG.instance = new DEBUG(world)
    }
  }

  public static get(): DEBUG {
    if (!DEBUG.instance) {
      console.error('Please instantiate debugger before use')
    }
    return DEBUG.instance
  }
}
